import type {
  OperationalAwardTypeCode,
  StatementList,
  StatementRow,
  TableSort,
} from '@myrewards/contracts';
import { TYPE_SHORT } from '../../ops/labels';
import { formatMoney, plural, type ListFilter, type SortColumn, type StatusTone } from '../../ui';
import { buysShares, netWords, PAYS_AS, paysAsWords } from '../labels';

// SA6: the Stock Admin's earning statement check. A statement that matches the estimate is
// confirmed; when Payroll's final tax changed an elected share count, the Stock Admin records
// Payroll's numbers with a reason, agreed with Payroll. Current claim ownership still applies.

/**
 * The note over the lines (SA6). There's no company rate (APP-101): only a PAA carries an
 * estimate, the Stock Admin team's figure from ADP Quick Calc.
 */
export function estimateNote(): string {
  return 'Estimate = the Stock Admin team’s ADP figure, PAA only · Payroll = from the earning statement';
}

/** A cash-only line's check, in words: there's no estimate to match. */
export const NO_ESTIMATE =
  'No estimate on a cash-only award: check Payroll’s figures against the statement.';

/**
 * A statement's file name, in pieces that wrap only after an underscore or a space
 * ("PAA-2026-0005_" "RKim.pdf"), so a name never breaks inside an award number.
 */
export function fileNameParts(name: string): string[] {
  return name.split(/(?<=[_ ])/).filter((part) => part !== '');
}

/** Which of Payroll's numbers differ, in words: "Tax and shares differ". */
export function differWords(differs: StatementRow['differs']): string | null {
  if (differs === null) return null;
  const parts = [
    differs.tax ? 'tax' : null,
    differs.cash ? 'cash' : null,
    differs.shares ? 'shares' : null,
  ].filter((p): p is string => p !== null);
  if (parts.length === 0) return null;
  const list =
    parts.length === 1
      ? parts[0]
      : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1] ?? ''}`;
  const sentence = `${list ?? ''} ${parts.length === 1 ? 'differs' : 'differ'}`;
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

/** The row's match status, never by colour alone. */
export function matchStatus(row: StatementRow): { label: string; tone: StatusTone } {
  switch (row.status) {
    case 'waiting_on_statement':
      return { label: 'Waiting on statement', tone: 'waiting' };
    case 'ready': {
      // A cash-only line has no estimate to match (APP-101): only the statement to check.
      if (row.estimate === null) return { label: 'Ready to confirm', tone: 'info' };
      const differs = differWords(row.differs);
      return {
        label: differs === null ? 'Matches · ready to confirm' : `${differs} · ready to confirm`,
        tone: 'info',
      };
    }
    case 'differs':
      return { label: differWords(row.differs) ?? 'Numbers differ', tone: 'problem' };
    case 'closed':
      return { label: row.overridden ? 'Recorded · closed' : 'Matches · closed', tone: 'done' };
    case 'to_shareworks':
      return {
        label: row.overridden ? 'Recorded · on to Shareworks' : 'Matches · on to Shareworks',
        tone: 'done',
      };
  }
}

/** Another Stock Admin is on this award's statement check right now. */
export function heldByOther(row: StatementRow, meId: string): boolean {
  return row.claimedBy !== null && row.claimedBy.id !== meId;
}

export type RowAction = 'confirm' | 'override' | 'held' | 'waiting' | 'done';

/** What the Stock Admin can do with a row now. */
export function rowAction(row: StatementRow, meId: string): RowAction {
  if (row.status === 'closed' || row.status === 'to_shareworks') return 'done';
  if (row.status === 'waiting_on_statement') return 'waiting';
  if (heldByOther(row, meId)) return 'held';
  return row.status === 'differs' ? 'override' : 'confirm';
}

/** The line to open first in the override panel: the oldest whose numbers differ. */
export function firstToSettle(list: StatementList, meId: string): StatementRow | null {
  return list.rows.find((r) => rowAction(r, meId) === 'override') ?? null;
}

/** What happens after a confirm or an override, for the dialog and the toast. */
export function nextStepWords(row: StatementRow, shares: number): string {
  if (row.options > 0 || shares > 0) return 'On to the Shareworks worklist';
  return 'The line closes';
}

/** "PAA-2026-0011 · Jordan Ellis · choice (c), tax withheld". */
export function overrideSubject(row: StatementRow): string {
  const pays =
    row.payAs === 'cash'
      ? 'cash only'
      : `${PAYS_AS[row.payAs].label.replace('PAA · ', '')}, ${PAYS_AS[row.payAs].detail.toLowerCase()}`;
  return `${row.award.displayId} · ${row.person.name} · ${pays}`;
}

export interface OverrideDraft {
  cashCents: number | null;
  taxCents: number | null;
  shares: number | null;
  reason: string;
  agreed: boolean;
}

/** The panel starts from Payroll's final numbers. */
export function overrideDraft(row: StatementRow): OverrideDraft {
  return {
    cashCents: row.final.cashCents,
    taxCents: row.final.taxCents,
    shares: row.final.shares,
    reason: '',
    agreed: false,
  };
}

export type FitCheck = 'fits' | 'off' | 'unknown';

/**
 * The rule the server applies (APP-043): the cash paid, the shares at the award's FMV and the tax
 * withheld add up to the award's cash exactly ((b)'s tax isn't withheld: the employee pays it).
 * No shares without a choice that buys them. `unknown` while the FMV is still loading. The (d)
 * ceiling isn't on the row: the server still checks it.
 */
export function fitCheck(
  row: StatementRow,
  draft: Pick<OverrideDraft, 'cashCents' | 'taxCents' | 'shares'>,
  fmvCents: number | null,
): FitCheck {
  const { cashCents, taxCents, shares } = draft;
  if (cashCents === null || taxCents === null || shares === null) return 'unknown';
  if (taxCents > row.grossCashCents) return 'off';
  if (shares > 0 && !buysShares(row.payAs)) return 'off';
  if (shares > 0 && fmvCents === null) return 'unknown';
  const stock = shares * (fmvCents ?? 0);
  const withheld = row.payAs === 'b' ? 0 : taxCents;
  return cashCents + stock + withheld === row.grossCashCents ? 'fits' : 'off';
}

/** Why "Record" is still off, in order, or null when it can be recorded. */
export function overrideBlocker(draft: OverrideDraft, fit: FitCheck): string | null {
  if (draft.cashCents === null || draft.taxCents === null || draft.shares === null)
    return 'Enter the final cash, tax and share count.';
  if (fit === 'off')
    return 'The cash paid, the shares at FMV and the tax withheld need to add up to the award’s cash.';
  if (draft.reason.trim() === '') return 'A reason is required.';
  if (!draft.agreed) return 'Tick “Agreed with Payroll” first.';
  return null;
}

/**
 * Payroll's numbers in a sentence: "Payroll withheld $37 more tax than the estimate, so $7,088
 * was left to buy shares: 90 whole shares at $77.95 ($7,015.50), with $72.50 paid in cash."
 */
export function settleSentence(row: StatementRow, fmvCents: number | null): string {
  const diff = row.estimate === null ? null : row.final.taxCents - row.estimate.taxCents;
  const tax =
    diff === null
      ? `Payroll withheld ${formatMoney(row.final.taxCents)}`
      : diff === 0
        ? 'Payroll withheld the estimated tax'
        : `Payroll withheld ${formatMoney(Math.abs(diff))} ${diff > 0 ? 'more' : 'less'} tax than the estimate`;
  if (!buysShares(row.payAs) || row.final.shares === 0)
    return `${tax}, so ${formatMoney(row.final.cashCents)} was paid in cash.`;
  const left = row.payAs === 'b' ? row.grossCashCents : row.grossCashCents - row.final.taxCents;
  const shares = plural(row.final.shares, 'whole share', 'whole shares');
  const stock =
    fmvCents === null
      ? shares
      : `${shares} at ${formatMoney(fmvCents)} (${formatMoney(row.final.shares * fmvCents)})`;
  return `${tax}, so ${formatMoney(left)} was left to buy shares: ${stock}, with ${formatMoney(row.final.cashCents)} paid in cash.`;
}

/** The four figures the panel compares: estimate above, Payroll's below. */
export function settleTiles(row: StatementRow): {
  label: string;
  estimate: string;
  payroll: string;
  differs: boolean;
}[] {
  // Only a PAA has an estimate; the panel opens for differing share counts, so a cash-only line
  // reaches here only by mistake, and says so rather than showing a figure it never had.
  const e = row.estimate;
  const none = 'none';
  return [
    {
      label: 'Cash portion',
      estimate: formatMoney(row.grossCashCents),
      payroll: formatMoney(row.grossCashCents),
      differs: false,
    },
    {
      label: 'Tax',
      estimate: e === null ? none : formatMoney(e.taxCents),
      payroll: formatMoney(row.final.taxCents),
      differs: row.differs?.tax ?? false,
    },
    {
      label: 'Shares',
      estimate: e === null ? none : String(e.shares),
      payroll: String(row.final.shares),
      differs: row.differs?.shares ?? false,
    },
    {
      label: 'Cash to employee',
      estimate: e === null ? none : netWords({ cashCents: e.cashCents, shares: 0 }),
      payroll: netWords({ cashCents: row.final.cashCents, shares: 0 }),
      differs: row.differs?.cash ?? false,
    },
  ];
}

// --- Sorting and filters (APP-149 D3-D5) ------------------------------------------------------

/** The lines open in award order, as they do today; ties keep each award's own order. */
export const STATEMENTS_DEFAULT_SORT: TableSort = { column: 'award', dir: 'asc' };
export const STATEMENTS_DEFAULT_WORDS = 'award order';
export const statementsTie = (row: StatementRow): string => row.award.displayId;

/** Where a line's match puts it: what needs settling, then what's ready, waiting, then done. */
const MATCH_RANK: Record<StatementRow['status'], number> = {
  differs: 0,
  ready: 1,
  waiting_on_statement: 2,
  to_shareworks: 3,
  closed: 4,
};

/**
 * The columns that sort. A cash-only line has no estimate: its estimate columns go last either
 * way.
 */
export const STATEMENT_SORT_COLUMNS: readonly SortColumn<StatementRow>[] = [
  { id: 'award', header: 'Award', sort: { value: (row) => row.award.displayId } },
  { id: 'employee', header: 'Employee', sort: { value: (row) => row.person.name } },
  {
    id: 'paysAs',
    header: 'Pays as',
    sort: { value: (row) => paysAsWords(row.payAs, row.type).label },
  },
  { id: 'cash', header: 'Cash', numeric: true, sort: { value: (row) => row.grossCashCents } },
  {
    id: 'estimateTax',
    header: 'Estimate tax',
    numeric: true,
    sort: { value: (row) => row.estimate?.taxCents ?? null },
  },
  {
    id: 'estimateCash',
    header: 'Estimate net cash',
    numeric: true,
    sort: { value: (row) => row.estimate?.cashCents ?? null },
  },
  {
    id: 'estimateShares',
    header: 'Estimate shares',
    numeric: true,
    sort: { value: (row) => row.estimate?.shares ?? null },
  },
  {
    id: 'payrollTax',
    header: 'Payroll tax',
    numeric: true,
    sort: { value: (row) => row.final.taxCents },
  },
  {
    id: 'payrollCash',
    header: 'Payroll net cash',
    numeric: true,
    sort: { value: (row) => row.final.cashCents },
  },
  {
    id: 'payrollShares',
    header: 'Payroll shares',
    numeric: true,
    sort: { value: (row) => row.final.shares },
  },
  {
    id: 'match',
    header: 'Match',
    sort: { value: (row) => MATCH_RANK[row.status], words: ['to settle first', 'done first'] },
  },
];

export type StatementFilterId = 'match' | 'type';

/** The filters that mean something on the statement check (D5): its match, and the award type. */
export function statementFilters(
  rows: readonly StatementRow[],
): Record<StatementFilterId, ListFilter<StatementRow>> {
  const types = (Object.keys(TYPE_SHORT) as OperationalAwardTypeCode[]).filter((code) =>
    rows.some((row) => row.type === code),
  );
  return {
    match: {
      name: 'Match',
      options: [
        { value: 'any', label: 'Any match' },
        { value: 'differs', label: 'Numbers differ' },
        { value: 'ready', label: 'Ready to confirm' },
        { value: 'waiting', label: 'Waiting on statement' },
        { value: 'done', label: 'Done' },
      ],
      passes: (row, value) =>
        value === 'waiting'
          ? row.status === 'waiting_on_statement'
          : value === 'done'
            ? row.status === 'closed' || row.status === 'to_shareworks'
            : row.status === value,
      listAll: true,
    },
    type: {
      name: 'Type',
      options: [
        { value: 'all', label: 'All types' },
        ...types.map((code) => ({ value: code, label: TYPE_SHORT[code] })),
      ],
      passes: (row, value) => row.type === value,
    },
  };
}

/** The params the statement check keeps in the address: its sort's and its filters'. */
export const STATEMENTS_PARAMS: readonly string[] = ['sort', 'dir', 'match', 'type'];
